/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * src/components/public/HubEsportivoLandingPage.tsx
 * 
 * Landing Page Institucional Pública do Hub Esportivo.
 * Apresenta a plataforma de gestão esportiva para visitantes, gestores de arenas e atletas
 * antes da autenticação e do acesso à aplicação.
 */

import React, { useState, useEffect } from "react";
import {
  Building2,
  CalendarDays,
  GraduationCap,
  Trophy,
  ShoppingCart,
  Clock,
  CircleDollarSign,
  Bell,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sun,
  Moon,
  Menu,
  X,
  Mail,
  Users,
  Sparkles,
  Layers,
  Percent,
  Calendar,
  Scale,
  CreditCard,
} from "lucide-react";
import { COMPANY_CONFIG, CNPJ_EMPRESA } from "../../config/companyConfig";
import { UserProfile, UserAccountType } from "../../types/userProfile";
import { AppTheme } from "../../types";
import { LandingPageConfigService } from "../../services/landingPageConfigService";
import {
  LandingPageConfig,
  DEFAULT_LANDING_PAGE_CONFIG,
} from "../../types/landingPageConfig";

export interface HubEsportivoLandingPageProps {
  onOpenGoogleAuth?: (role?: UserAccountType) => void;
  onGoToHub?: () => void;
  isLoggedIn?: boolean;
  userProfile?: UserProfile | null;
  theme?: AppTheme;
  setTheme?: React.Dispatch<React.SetStateAction<AppTheme>> | ((theme: AppTheme) => void);
}

export const HubEsportivoLandingPage: React.FC<HubEsportivoLandingPageProps> = ({
  onOpenGoogleAuth,
  onGoToHub,
  isLoggedIn = false,
  userProfile,
  theme = "light",
  setTheme,
}) => {
  const [landingConfig, setLandingConfig] = useState<LandingPageConfig>(
    DEFAULT_LANDING_PAGE_CONFIG
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = LandingPageConfigService.subscribeLandingPageConfig(
      (config) => {
        setLandingConfig(config);
      }
    );
    return () => unsubscribe();
  }, []);

  const toggleTheme = () => {
    if (setTheme) {
      setTheme(theme === "light" ? "dark" : "light");
    }
  };

  const handleAuthAction = (role?: UserAccountType) => {
    if (onOpenGoogleAuth) {
      onOpenGoogleAuth(role);
    }
  };

  const handlePlatformAction = () => {
    if (onGoToHub) {
      onGoToHub();
    }
  };

  const scrollToSection = (id: string) => {
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div
      className={`min-h-screen w-full transition-colors duration-200 font-sans selection:bg-amber-400 selection:text-emerald-950 ${
        theme === "light"
          ? "bg-slate-50 text-slate-900"
          : "bg-zinc-950 text-zinc-100"
      }`}
    >
      {/* =========================================================================
          1. NAVBAR INSTITUCIONAL SUPERIOR
          ========================================================================= */}
      <header
        className={`sticky top-0 z-40 w-full backdrop-blur-md border-b transition-colors ${
          theme === "light"
            ? "bg-white/90 border-slate-200"
            : "bg-zinc-950/90 border-zinc-800"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
          {/* Logo Oficial */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex items-center gap-3 shrink-0 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-emerald-500 rounded-lg"
            aria-label="Hub Esportivo - Página Inicial"
          >
            <img
              src="/branding/hub-esportivo-logo.svg"
              alt="Logo Oficial Hub Esportivo"
              className="h-12 sm:h-14 w-auto object-contain"
            />
          </a>

          {/* Links de Navegação Desktop */}
          <nav
            aria-label="Navegação Institucional"
            className="hidden lg:flex items-center gap-6 text-sm font-medium"
          >
            <button
              type="button"
              onClick={() => scrollToSection("quem-somos")}
              className={`hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer ${
                theme === "light" ? "text-slate-600" : "text-zinc-400"
              }`}
            >
              Quem Somos
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("objetivo")}
              className={`hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer ${
                theme === "light" ? "text-slate-600" : "text-zinc-400"
              }`}
            >
              Nosso Objetivo
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("para-quem-e")}
              className={`hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer ${
                theme === "light" ? "text-slate-600" : "text-zinc-400"
              }`}
            >
              Para Quem É
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("modulos")}
              className={`hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer ${
                theme === "light" ? "text-slate-600" : "text-zinc-400"
              }`}
            >
              Módulos
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("modelos-comerciais")}
              className={`hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer ${
                theme === "light" ? "text-slate-600" : "text-zinc-400"
              }`}
            >
              Modelos Comerciais
            </button>
            <button
              type="button"
              onClick={() => scrollToSection("empresa")}
              className={`hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors cursor-pointer ${
                theme === "light" ? "text-slate-600" : "text-zinc-400"
              }`}
            >
              Empresa
            </button>
          </nav>

          {/* Botões de Ação & Tema */}
          <div className="flex items-center gap-3">
            {/* Alternador de Tema */}
            {setTheme && (
              <button
                type="button"
                onClick={toggleTheme}
                className={`p-2.5 rounded-xl border transition-colors cursor-pointer ${
                  theme === "light"
                    ? "bg-slate-100 hover:bg-slate-200 border-slate-200 text-slate-700"
                    : "bg-zinc-900 hover:bg-zinc-800 border-zinc-800 text-zinc-300"
                }`}
                aria-label={`Alternar para tema ${theme === "light" ? "escuro" : "claro"}`}
              >
                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              </button>
            )}

            {/* Ações de Entrada */}
            {isLoggedIn ? (
              <button
                type="button"
                onClick={handlePlatformAction}
                className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors shadow-xs cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                <span>Acessar Plataforma</span>
                <ArrowRight size={16} />
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleAuthAction}
                  className={`hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors cursor-pointer ${
                    theme === "light"
                      ? "bg-white hover:bg-slate-100 border-slate-300 text-slate-800"
                      : "bg-zinc-900 hover:bg-zinc-800 border-zinc-700 text-zinc-200"
                  }`}
                >
                  <span>Entrar</span>
                </button>
                <button
                  type="button"
                  onClick={handlePlatformAction}
                  className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors shadow-xs cursor-pointer"
                >
                  <span>Conhecer a plataforma</span>
                  <ArrowRight size={16} />
                </button>
              </>
            )}

            {/* Botão Menu Mobile */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden p-2.5 rounded-xl border transition-colors cursor-pointer ${
                theme === "light"
                  ? "bg-slate-100 border-slate-200 text-slate-800"
                  : "bg-zinc-900 border-zinc-800 text-zinc-200"
              }`}
              aria-label={mobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Menu Mobile Retrátil */}
        {mobileMenuOpen && (
          <div
            className={`lg:hidden border-b px-4 py-6 space-y-4 animate-in slide-in-from-top-2 duration-150 ${
              theme === "light" ? "bg-white border-slate-200" : "bg-zinc-950 border-zinc-800"
            }`}
          >
            <nav className="flex flex-col space-y-3 text-sm font-medium">
              <button
                type="button"
                onClick={() => scrollToSection("quem-somos")}
                className="text-left py-2 text-slate-700 dark:text-zinc-300 hover:text-emerald-600"
              >
                Quem Somos
              </button>
              <button
                type="button"
                onClick={() => scrollToSection("objetivo")}
                className="text-left py-2 text-slate-700 dark:text-zinc-300 hover:text-emerald-600"
              >
                Nosso Objetivo
              </button>
              <button
                type="button"
                onClick={() => scrollToSection("para-quem-e")}
                className="text-left py-2 text-slate-700 dark:text-zinc-300 hover:text-emerald-600"
              >
                Para Quem É
              </button>
              <button
                type="button"
                onClick={() => scrollToSection("modulos")}
                className="text-left py-2 text-slate-700 dark:text-zinc-300 hover:text-emerald-600"
              >
                Módulos da Plataforma
              </button>
              <button
                type="button"
                onClick={() => scrollToSection("modelos-comerciais")}
                className="text-left py-2 text-slate-700 dark:text-zinc-300 hover:text-emerald-600"
              >
                Modelos Comerciais
              </button>
              <button
                type="button"
                onClick={() => scrollToSection("empresa")}
                className="text-left py-2 text-slate-700 dark:text-zinc-300 hover:text-emerald-600"
              >
                Informações da Empresa
              </button>
            </nav>

            <div className="pt-4 border-t border-slate-200 dark:border-zinc-800 flex flex-col gap-2">
              {isLoggedIn ? (
                <button
                  type="button"
                  onClick={handlePlatformAction}
                  className="w-full py-3 px-4 rounded-xl bg-emerald-600 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-xs"
                >
                  <span>Acessar Plataforma</span>
                  <ArrowRight size={16} />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleAuthAction}
                    className="w-full py-3 px-4 rounded-xl bg-emerald-600 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-xs"
                  >
                    <span>Entrar / Criar conta</span>
                  </button>
                  <button
                    type="button"
                    onClick={handlePlatformAction}
                    className={`w-full py-3 px-4 rounded-xl font-semibold text-sm border flex items-center justify-center gap-2 ${
                      theme === "light"
                        ? "bg-slate-100 border-slate-300 text-slate-800"
                        : "bg-zinc-900 border-zinc-700 text-zinc-200"
                    }`}
                  >
                    <span>Conhecer a plataforma</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* =========================================================================
          2. HERO — PRIMEIRA IMPRESSÃO
          ========================================================================= */}
      <section className="relative overflow-hidden pt-12 pb-20 lg:pt-20 lg:pb-28 border-b border-slate-200 dark:border-zinc-800">
        {/* Gradiente sutil de fundo sem poluição visual */}
        <div
          aria-hidden="true"
          className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-gradient-to-b from-emerald-500/10 via-amber-500/5 to-transparent pointer-events-none -z-10 blur-3xl opacity-70"
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Texto Principal & CTAs */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              {/* Metadado editorial limpo sem pílulas artificiais */}
              <div className="flex items-center justify-center lg:justify-start gap-2 text-xs font-semibold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">
                <span>{landingConfig.institutional?.brandName || COMPANY_CONFIG.brandName}</span>
                <span aria-hidden="true">·</span>
                <span>{landingConfig.hero?.eyebrow || DEFAULT_LANDING_PAGE_CONFIG.hero.eyebrow}</span>
              </div>

              {/* Título Principal */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-slate-950 dark:text-white leading-[1.15]">
                {landingConfig.hero?.title || DEFAULT_LANDING_PAGE_CONFIG.hero.title}
              </h1>

              {/* Texto Complementar */}
              <p className="text-base sm:text-lg text-slate-600 dark:text-zinc-300 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                {landingConfig.hero?.subtitle || DEFAULT_LANDING_PAGE_CONFIG.hero.subtitle}
              </p>

              {/* Grupo de CTAs Principais */}
              <div className="pt-2 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3.5">
                <button
                  type="button"
                  onClick={handlePlatformAction}
                  className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-emerald-500"
                >
                  <span>{landingConfig.hero?.primaryCtaLabel || DEFAULT_LANDING_PAGE_CONFIG.hero.primaryCtaLabel}</span>
                  <ArrowRight size={16} />
                </button>

                <button
                  type="button"
                  onClick={handleAuthAction}
                  className={`w-full sm:w-auto px-6 py-3.5 rounded-xl font-bold text-sm border transition-all flex items-center justify-center gap-2 cursor-pointer ${
                    theme === "light"
                      ? "bg-white hover:bg-slate-100 border-slate-300 text-slate-800 shadow-xs"
                      : "bg-zinc-900 hover:bg-zinc-800 border-zinc-700 text-zinc-200"
                  }`}
                >
                  <span>Entrar</span>
                </button>

                <button
                  type="button"
                  onClick={() => scrollToSection("para-quem-e")}
                  className={`w-full sm:w-auto px-6 py-3.5 rounded-xl font-semibold text-sm border transition-all flex items-center justify-center gap-2 cursor-pointer ${
                    theme === "light"
                      ? "bg-amber-50 hover:bg-amber-100/80 border-amber-200 text-amber-900"
                      : "bg-amber-950/30 hover:bg-amber-950/50 border-amber-800/40 text-amber-300"
                  }`}
                >
                  <span>{landingConfig.hero?.arenaCtaLabel || DEFAULT_LANDING_PAGE_CONFIG.hero.arenaCtaLabel}</span>
                  <ChevronRight size={15} />
                </button>
              </div>

              {/* Indicadores de Confiabilidade Sem Alegações Fictícias */}
              <div className="pt-4 flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 text-xs text-slate-500 dark:text-zinc-400">
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={15} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>Ambiente público sem login para consultas</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={15} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>Modelos comerciais flexíveis</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <CheckCircle2 size={15} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                  <span>Acesso web & PWA em qualquer dispositivo</span>
                </div>
              </div>
            </div>

            {/* Showcase Visual: Painel Realista do Hub Esportivo */}
            <div className="lg:col-span-5">
              <div
                className={`rounded-2xl border p-5 shadow-xl space-y-4 transition-colors ${
                  theme === "light"
                    ? "bg-white border-slate-200"
                    : "bg-zinc-900/90 border-zinc-800"
                }`}
              >
                {/* Cabeçalho da Mini Janela */}
                <div className="flex items-center justify-between pb-3 border-b border-slate-200 dark:border-zinc-800">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-rose-500/80" />
                    <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                    <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                    <span className="ml-2 text-xs font-bold text-slate-500 dark:text-zinc-400">
                      Painel Operacional da Arena
                    </span>
                  </div>
                  <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                    Sistema Ativo
                  </span>
                </div>

                {/* Blocos de Demonstração dos Módulos Operacionais */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-zinc-950 border border-slate-200/80 dark:border-zinc-800">
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mb-1">
                      <span>Capacidade de Quadras</span>
                      <Building2 size={14} className="text-emerald-500" />
                    </div>
                    <p className="text-base font-bold text-slate-900 dark:text-white">
                      {landingConfig.hero?.capabilityTitle || DEFAULT_LANDING_PAGE_CONFIG.hero.capabilityTitle}
                    </p>
                    <span className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                      {landingConfig.hero?.capabilityDescription || DEFAULT_LANDING_PAGE_CONFIG.hero.capabilityDescription}
                    </span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-zinc-950 border border-slate-200/80 dark:border-zinc-800">
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mb-1">
                      <span>Reservas do Dia</span>
                      <CalendarDays size={14} className="text-sky-500" />
                    </div>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">Agenda Central</p>
                    <span className="text-[11px] text-sky-600 dark:text-sky-400 font-medium">
                      Avulsos, horários fixos e aulas
                    </span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-zinc-950 border border-slate-200/80 dark:border-zinc-800">
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mb-1">
                      <span>Frente de Caixa (PDV)</span>
                      <ShoppingCart size={14} className="text-amber-500" />
                    </div>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">Vendas & Bar</p>
                    <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                      Controle de estoque e consumo
                    </span>
                  </div>

                  <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-zinc-950 border border-slate-200/80 dark:border-zinc-800">
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mb-1">
                      <span>Torneios & Eventos</span>
                      <Trophy size={14} className="text-purple-500" />
                    </div>
                    <p className="text-lg font-bold text-slate-900 dark:text-white">Chaveamentos</p>
                    <span className="text-[11px] text-purple-600 dark:text-purple-400 font-medium">
                      Grupos, mata-mata e inscrições
                    </span>
                  </div>
                </div>

                {/* Notificação Operacional de Demonstração */}
                <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/40 flex items-start gap-3 text-xs">
                  <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="font-bold text-emerald-900 dark:text-emerald-200">
                      Fluxo Financeiro e Conciliação
                    </p>
                    <p className="text-emerald-700 dark:text-emerald-300">
                      Modelos de Split Automático, Mensalidade Fixa ou Híbrido integrados à rotina da arena.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          3. SEÇÃO "QUEM SOMOS"
          ========================================================================= */}
      <section
        id="quem-somos"
        className="py-16 lg:py-24 border-b border-slate-200 dark:border-zinc-800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="max-w-3xl">
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Institucional
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 dark:text-white mt-1">
              {landingConfig.about?.title || DEFAULT_LANDING_PAGE_CONFIG.about.title}
            </h2>
            <p className="mt-4 text-base text-slate-600 dark:text-zinc-300 leading-relaxed">
              {landingConfig.about?.description || DEFAULT_LANDING_PAGE_CONFIG.about.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                <Building2 size={20} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Foco no Setor Esportivo
              </h3>
              <p className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">
                Projetado especificamente para as particularidades de quadras de areia, society, tênis, padel e centros esportivos que precisam de controle de disponibilidade, horários e mensalistas.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center font-bold">
                <Layers size={20} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Centralização de Ferramentas
              </h3>
              <p className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">
                Substitui a pulverização de dados entre planilhas avulsas, cadernos e mensagens de aplicativo, reunindo agendamentos, vendas e torneios em um único sistema acessível.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold">
                <Users size={20} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Gestão e Atletas Conectados
              </h3>
              <p className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">
                Oferece autonomia para o dono da arena configurar suas regras de negócio e facilidade para os atletas localizarem horários, agendarem partidas e acompanharem competições.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          4. SEÇÃO "QUAL É O NOSSO OBJETIVO"
          ========================================================================= */}
      <section
        id="objetivo"
        className="py-16 lg:py-24 border-b border-slate-200 dark:border-zinc-800 bg-slate-100/50 dark:bg-zinc-900/40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="max-w-3xl">
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Propósito
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 dark:text-white mt-1">
              {landingConfig.objective?.title || DEFAULT_LANDING_PAGE_CONFIG.objective.title}
            </h2>
            <p className="mt-4 text-base text-slate-600 dark:text-zinc-300 leading-relaxed">
              {landingConfig.objective?.description || DEFAULT_LANDING_PAGE_CONFIG.objective.description}
            </p>
          </div>

          {/* Grid dos 8 Pilares Estruturais do Objetivo */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">01</span>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Organização</h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                Controle visual e transparente da grade de horários, disponibilidade de quadras e turnos.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <span className="text-xs font-bold text-sky-600 dark:text-sky-400">02</span>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Reservas</h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                Agendamentos rápidos de partidas avulsas com confirmação e controle de capacidade.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <span className="text-xs font-bold text-amber-600 dark:text-amber-400">03</span>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Operação</h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                Gestão de intervalos, bloqueios para manutenção e parametrizações operacionais por quadra.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <span className="text-xs font-bold text-rose-600 dark:text-rose-400">04</span>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Vendas & PDV</h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                Frente de caixa para bebidas, conveniência, aluguel de materiais e controle de estoque.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <span className="text-xs font-bold text-purple-600 dark:text-purple-400">05</span>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Eventos & Aulas</h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                Coordenação de aulas com professores, turmas fixas e eventos recreativos da arena.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400">06</span>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Torneios</h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                Estruturação de campeonatos com inscrições, fase de grupos, playoffs e classificação.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">07</span>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Gestão Financeira</h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                Conciliação das cobranças e modelos comerciais adequados ao perfil de cada espaço.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2">
              <span className="text-xs font-bold text-teal-600 dark:text-teal-400">08</span>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">Experiência do Atleta</h3>
              <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                Facilidade para encontrar quadras, conferir disponibilidade e participar de jogos e torneios.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          5. SEÇÃO "PARA QUEM É"
          ========================================================================= */}
      <section
        id="para-quem-e"
        className="py-16 lg:py-24 border-b border-slate-200 dark:border-zinc-800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Público-Alvo
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 dark:text-white mt-1">
              Desenvolvido para atender dois públicos fundamentais
            </h2>
            <p className="mt-3 text-base text-slate-600 dark:text-zinc-400">
              Soluções completas para quem administra o espaço e conveniência para quem joga e treina.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Bloco: Donos de Arenas */}
            <div className="p-8 rounded-3xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-sm flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold">
                    <Building2 size={24} />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
                      Gestão Profissional
                    </span>
                    <h3 className="text-xl font-black text-slate-950 dark:text-white">
                      {landingConfig.audiences?.arenaOwner?.title || DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.title}
                    </h3>
                  </div>
                </div>

                <p className="text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                  {landingConfig.audiences?.arenaOwner?.description || DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.description}
                </p>

                <div className="space-y-2.5 pt-2">
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Administrar quadras esportivas e suas características técnicas</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Configurar horários de funcionamento, intervalos e valores por hora</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Controlar reservas avulsas e bloqueios operacionais</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Gerenciar aulas, treinos e atividades com instrutores</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Administrar mensalistas e horários fixos semanais</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Vender produtos na recepção e operar frente de caixa (PDV)</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Controlar estoque com alertas de reposição mínima</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Organizar eventos recreativos e corporativos</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Gerenciar torneios oficiais com inscrições e tabelas</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Acompanhar informações financeiras e modelos comerciais</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                    <span>Acompanhar notificações operacionais e avisos em tempo real</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-zinc-800">
                <button
                  type="button"
                  onClick={() => handleAuthAction("dono_arena")}
                  className="w-full py-3 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>{landingConfig.audiences?.arenaOwner?.ctaLabel || DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.ctaLabel}</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Bloco: Atletas */}
            <div className="p-8 rounded-3xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-sm flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center font-bold">
                    <Users size={24} />
                  </div>
                  <div>
                    <span className="text-xs font-bold text-sky-600 dark:text-sky-400 uppercase tracking-wide">
                      Praticantes & Jogadores
                    </span>
                    <h3 className="text-xl font-black text-slate-950 dark:text-white">
                      {landingConfig.audiences?.athlete?.title || DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.title}
                    </h3>
                  </div>
                </div>

                <p className="text-sm text-slate-600 dark:text-zinc-300 leading-relaxed">
                  {landingConfig.audiences?.athlete?.description || DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.description}
                </p>

                <div className="space-y-2.5 pt-2">
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                    <span>Encontrar quadras e arenas esportivas disponíveis</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                    <span>Realizar reservas com visualização de disponibilidade</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                    <span>Acompanhar seus agendamentos passados e futuros</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                    <span>Participar de jogos, rachões e sorteios inteligentes de duplas</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                    <span>Acompanhar torneios, chaveamentos e resultados oficiais</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                    <span>Participar de atividades e treinos organizados pelas arenas</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                    <span>Receber notificações e confirmações de horários agendados</span>
                  </div>
                  <div className="flex items-start gap-2.5 text-xs text-slate-700 dark:text-zinc-300">
                    <CheckCircle2 size={16} className="text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
                    <span>Acompanhar seus registros e histórico de partidas dentro da plataforma</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-zinc-800">
                <button
                  type="button"
                  onClick={handlePlatformAction}
                  className="w-full py-3 px-4 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-bold text-sm transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>{landingConfig.audiences?.athlete?.ctaLabel || DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.ctaLabel}</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          6. SEÇÃO "TUDO EM UMA PLATAFORMA" (MÓDULOS REAIS)
          ========================================================================= */}
      <section
        id="modulos"
        className="py-16 lg:py-24 border-b border-slate-200 dark:border-zinc-800 bg-slate-100/50 dark:bg-zinc-900/40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Arquitetura Completa
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 dark:text-white mt-1">
              Tudo em uma plataforma
            </h2>
            <p className="mt-3 text-base text-slate-600 dark:text-zinc-400">
              Conheça os módulos integrados que tornam a gestão de arenas ágil e profissional.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(landingConfig.modules || DEFAULT_LANDING_PAGE_CONFIG.modules)
              .filter((mod) => mod.enabled !== false)
              .map((mod) => {
                let icon = <Layers size={22} />;
                let colorClass = "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:border-emerald-500/50";

                if (mod.id === "courts") {
                  icon = <Building2 size={22} />;
                  colorClass = "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:border-emerald-500/50";
                } else if (mod.id === "bookings") {
                  icon = <CalendarDays size={22} />;
                  colorClass = "bg-sky-500/10 text-sky-600 dark:text-sky-400 hover:border-sky-500/50";
                } else if (mod.id === "lessons") {
                  icon = <GraduationCap size={22} />;
                  colorClass = "bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:border-amber-500/50";
                } else if (mod.id === "tournaments") {
                  icon = <Trophy size={22} />;
                  colorClass = "bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:border-purple-500/50";
                } else if (mod.id === "pdv") {
                  icon = <ShoppingCart size={22} />;
                  colorClass = "bg-rose-500/10 text-rose-600 dark:text-rose-400 hover:border-rose-500/50";
                } else if (mod.id === "schedule") {
                  icon = <Clock size={22} />;
                  colorClass = "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:border-indigo-500/50";
                } else if (mod.id === "finance") {
                  icon = <CircleDollarSign size={22} />;
                  colorClass = "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:border-emerald-500/50";
                } else if (mod.id === "notifications") {
                  icon = <Bell size={22} />;
                  colorClass = "bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:border-amber-500/50";
                }

                return (
                  <div
                    key={mod.id}
                    className={`p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-3 transition-colors ${colorClass}`}
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-current/10">
                      {icon}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 dark:text-white">
                      {mod.title}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                      {mod.description}
                    </p>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* =========================================================================
          7. SEÇÃO "MODELOS COMERCIAIS"
          ========================================================================= */}
      <section
        id="modelos-comerciais"
        className="py-16 lg:py-24 border-b border-slate-200 dark:border-zinc-800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Flexibilidade Comercial
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 dark:text-white mt-1">
              {landingConfig.commercialModels?.title || DEFAULT_LANDING_PAGE_CONFIG.commercialModels.title}
            </h2>
            <p className="mt-3 text-base text-slate-600 dark:text-zinc-400 leading-relaxed">
              {landingConfig.commercialModels?.description || DEFAULT_LANDING_PAGE_CONFIG.commercialModels.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(landingConfig.commercialModels?.models || DEFAULT_LANDING_PAGE_CONFIG.commercialModels.models)
              .filter((cm) => cm.enabled !== false)
              .map((cm) => {
                let badge = "Modelo Baseado em Performance";
                let icon = <Percent size={22} />;
                let borderClass = "border border-slate-200 dark:border-zinc-800";
                let badgeColor = "text-emerald-600 dark:text-emerald-400";
                let iconBg = "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
                let buttonStyle = "border border-emerald-500/30 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300";

                if (cm.id === "FIXED_MONTHLY") {
                  badge = "Previsibilidade Total";
                  icon = <Calendar size={22} />;
                  borderClass = "border-2 border-emerald-500/50 shadow-md";
                  buttonStyle = "bg-emerald-600 hover:bg-emerald-500 text-white shadow-xs";
                } else if (cm.id === "HYBRID") {
                  badge = "Equilíbrio Operacional";
                  icon = <Scale size={22} />;
                  badgeColor = "text-amber-600 dark:text-amber-400";
                  iconBg = "bg-amber-500/10 text-amber-600 dark:text-amber-400";
                  buttonStyle = "border border-amber-500/30 hover:bg-amber-50 dark:hover:bg-amber-950/20 text-amber-700 dark:text-amber-300";
                }

                return (
                  <div
                    key={cm.id}
                    className={`p-8 rounded-3xl bg-white dark:bg-zinc-900 ${borderClass} flex flex-col justify-between space-y-6 hover:shadow-lg transition-all`}
                  >
                    <div className="space-y-4">
                      <div className={`w-12 h-12 rounded-2xl ${iconBg} flex items-center justify-center font-bold`}>
                        {icon}
                      </div>
                      <div>
                        <span className={`text-xs font-bold ${badgeColor} uppercase tracking-wide`}>
                          {badge}
                        </span>
                        <h3 className="text-xl font-black text-slate-950 dark:text-white mt-0.5">
                          {cm.title}
                        </h3>
                      </div>
                      <p className="text-sm font-semibold text-slate-800 dark:text-zinc-200">
                        {cm.subtitle}
                      </p>
                      <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                        {cm.description}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </section>

      {/* =========================================================================
          8. SEÇÃO "POR QUE USAR"
          ========================================================================= */}
      <section
        id="por-que-usar"
        className="py-16 lg:py-24 border-b border-slate-200 dark:border-zinc-800 bg-slate-100/50 dark:bg-zinc-900/40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="max-w-3xl">
            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Diferenciais Reais
            </span>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-950 dark:text-white mt-1">
              {landingConfig.benefits?.title || DEFAULT_LANDING_PAGE_CONFIG.benefits.title}
            </h2>
            <p className="mt-4 text-base text-slate-600 dark:text-zinc-300 leading-relaxed">
              {landingConfig.benefits?.description || DEFAULT_LANDING_PAGE_CONFIG.benefits.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(landingConfig.benefits?.items || DEFAULT_LANDING_PAGE_CONFIG.benefits.items)
              .filter((b) => b.enabled !== false)
              .map((b) => (
                <div
                  key={b.id}
                  className="p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 space-y-2"
                >
                  <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                    <span>{b.title}</span>
                  </h3>
                  <p className="text-xs text-slate-600 dark:text-zinc-400 leading-relaxed">
                    {b.description}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* =========================================================================
          9. SEÇÃO EMPRESA & DADOS CORPORATIVOS
          ========================================================================= */}
      <section
        id="empresa"
        className="py-16 lg:py-20 border-b border-slate-200 dark:border-zinc-800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 sm:p-10 rounded-3xl bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 shadow-sm space-y-6">
            <div className="max-w-2xl space-y-2">
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                {landingConfig.contact?.title || DEFAULT_LANDING_PAGE_CONFIG.contact.title}
              </span>
              <h2 className="text-2xl font-black text-slate-950 dark:text-white">
                {landingConfig.institutional?.brandName || COMPANY_CONFIG.brandName}
              </h2>
              <p className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">
                {landingConfig.contact?.description || DEFAULT_LANDING_PAGE_CONFIG.contact.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4 border-t border-slate-200 dark:border-zinc-800 text-sm">
              <div className="space-y-1">
                <span className="text-xs text-slate-500 dark:text-zinc-400 font-semibold block">
                  Marca
                </span>
                <p className="font-bold text-slate-900 dark:text-white">
                  {landingConfig.institutional?.brandName || COMPANY_CONFIG.brandName}
                </p>
              </div>

              <div className="space-y-1">
                <span className="text-xs text-slate-500 dark:text-zinc-400 font-semibold block">
                  E-mail Oficial
                </span>
                <a
                  href={`mailto:${landingConfig.institutional?.officialEmail || COMPANY_CONFIG.email}`}
                  className="font-bold text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-1.5"
                >
                  <Mail size={16} />
                  <span>{landingConfig.institutional?.officialEmail || COMPANY_CONFIG.email}</span>
                </a>
              </div>

              <div className="space-y-1">
                <span className="text-xs text-slate-500 dark:text-zinc-400 font-semibold block">
                  CNPJ
                </span>
                <p className="font-bold text-slate-900 dark:text-white">
                  {landingConfig.institutional?.cnpj || CNPJ_EMPRESA}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          10. CTA FINAL
          ========================================================================= */}
      <section className="py-20 lg:py-28 relative overflow-hidden bg-gradient-to-b from-slate-100/60 via-emerald-500/5 to-transparent dark:from-zinc-900/60 dark:via-emerald-950/20 dark:to-transparent border-b border-slate-200 dark:border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
            Comece Agora
          </span>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-950 dark:text-white tracking-tight">
            {landingConfig.finalCta?.title || DEFAULT_LANDING_PAGE_CONFIG.finalCta.title}
          </h2>
          <p className="text-base sm:text-lg text-slate-600 dark:text-zinc-300 leading-relaxed max-w-2xl mx-auto">
            {landingConfig.finalCta?.subtitle || DEFAULT_LANDING_PAGE_CONFIG.finalCta.subtitle}
          </p>

          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={handleAuthAction}
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 cursor-pointer focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              <span>Entrar / Criar conta</span>
              <ArrowRight size={16} />
            </button>

            <button
              type="button"
              onClick={() => handleAuthAction("dono_arena")}
              className={`w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-sm border transition-all flex items-center justify-center gap-2 cursor-pointer ${
                theme === "light"
                  ? "bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-900"
                  : "bg-amber-950/40 hover:bg-amber-950/60 border-amber-800/40 text-amber-300"
              }`}
            >
              <span>{landingConfig.hero?.arenaCtaLabel || DEFAULT_LANDING_PAGE_CONFIG.hero.arenaCtaLabel}</span>
            </button>

            <button
              type="button"
              onClick={handlePlatformAction}
              className={`w-full sm:w-auto px-8 py-4 rounded-xl font-semibold text-sm border transition-all flex items-center justify-center gap-2 cursor-pointer ${
                theme === "light"
                  ? "bg-white hover:bg-slate-100 border-slate-300 text-slate-800"
                  : "bg-zinc-900 hover:bg-zinc-800 border-zinc-700 text-zinc-200"
              }`}
            >
              <span>{landingConfig.hero?.primaryCtaLabel || DEFAULT_LANDING_PAGE_CONFIG.hero.primaryCtaLabel}</span>
            </button>
          </div>
        </div>
      </section>

      {/* =========================================================================
          11. FOOTER INSTITUCIONAL
          ========================================================================= */}
      <footer
        className={`py-12 px-4 sm:px-6 lg:px-8 text-xs transition-colors ${
          theme === "light"
            ? "bg-white text-slate-600 border-slate-200"
            : "bg-zinc-950 text-zinc-400 border-zinc-800"
        }`}
      >
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pb-8 border-b border-slate-200 dark:border-zinc-800">
            {/* Bloco de Marca */}
            <div className="space-y-2">
              <img
                src="/branding/hub-esportivo-logo.svg"
                alt="Hub Esportivo"
                className="h-10 w-auto object-contain"
              />
              <p className="text-xs text-slate-500 dark:text-zinc-400 max-w-md">
                {landingConfig.footer?.description || DEFAULT_LANDING_PAGE_CONFIG.footer.description}
              </p>
            </div>

            {/* Contato Direto */}
            <div className="flex items-center gap-3">
              <a
                href={`mailto:${landingConfig.institutional?.officialEmail || COMPANY_CONFIG.email}`}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 dark:bg-zinc-900 text-slate-800 dark:text-zinc-200 hover:text-emerald-600 dark:hover:text-emerald-400 font-semibold transition-colors"
              >
                <Mail size={15} />
                <span>{landingConfig.institutional?.officialEmail || COMPANY_CONFIG.email}</span>
              </a>
            </div>
          </div>

          {/* Dados Legais e Copyright */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left text-[11px] text-slate-500 dark:text-zinc-500">
            <div className="space-y-1">
              <p className="font-semibold text-slate-700 dark:text-zinc-300">
                {landingConfig.institutional?.brandName || COMPANY_CONFIG.brandName}
              </p>
              <p>
                CNPJ: {landingConfig.institutional?.cnpj || CNPJ_EMPRESA}
              </p>
            </div>

            <p>
              © {COMPANY_CONFIG.year} {landingConfig.institutional?.brandName || COMPANY_CONFIG.brandName}. {landingConfig.footer?.copyright || DEFAULT_LANDING_PAGE_CONFIG.footer.copyright}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HubEsportivoLandingPage;
